<template lang="pug">
.bulk-translation-jobs.flex.flex-col.h-screen.w-screen.bg-white.dark_bg-gray-700
  .flex.gap-2.p-2.z-50.bg-gray-100.dark_bg-gray-800.h-14.items-center
    MainMenu
      .flex.flex-col
        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(href="#/home")
          | Home – all courses
        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(href="#/bulk-audio")
          | Bulk Audio
        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(href="#/bulk-customisations")
          | Bulk Customisations
    .ms-auto
    ToggleDark

  .flex-1.p-24.overflow-y-auto.overflow-x-visible(ref="scrollContainer")
    .max-w-screen-2xl.mx-auto
      .prose.dark_prose-invert.max-w-none
        .flex.items-center.gap-4.mb-6
          h1.font-normal.m-0 Bulk Translation Jobs

          //- Refresh + auto-refresh control
          .inline-flex.items-stretch.rounded-md.border.border-gray-300.dark_border-gray-600
            button.flex.items-center.justify-center.px-3.py-2.rounded-l-md.bg-white.dark_bg-gray-800.hover_bg-gray-100.dark_hover_bg-gray-700.text-gray-700.dark_text-gray-200.disabled_opacity-50(
              type="button"
              title="Refresh"
              @click="load"
              :disabled="loading"
            )
              Icon.text-lg(icon="ph:arrows-clockwise" :class="loading ? 'animate-spin' : ''")

            HeadlessMenu.relative(as="div")
              MenuButton.flex.items-center.gap-1.h-full.px-3.py-2.rounded-r-md.border-0.border-l.border-gray-300.dark_border-gray-600.bg-white.dark_bg-gray-800.hover_bg-gray-100.dark_hover_bg-gray-700.text-sm.text-gray-700.dark_text-gray-200
                Icon(icon="ph:caret-down")
                span {{ autoRefreshLabel }}

              transition(
                enter-active-class="transition duration-100 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-75 ease-in"
                leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0"
              )
                MenuItems.absolute.right-0.mt-2.w-48.origin-top-right.rounded-md.bg-white.dark_bg-gray-800.shadow-lg.ring-1.ring-black.ring-opacity-5.z-50.py-1(
                  class="focus:outline-none"
                )
                  MenuItem(v-for="opt in autoRefreshOptions" :key="opt.value" v-slot="{ active }")
                    button.flex.w-full.items-center.gap-2.px-4.py-2.text-sm.text-left.text-gray-700.dark_text-gray-200(
                      type="button"
                      :class="active ? 'bg-gray-100 dark_bg-gray-700' : ''"
                      @click="setAutoRefresh(opt.value)"
                    )
                      span.w-4.flex-none
                        Icon(v-if="autoRefreshMs === opt.value" icon="ph:circle-fill" class="text-[8px]")
                      span {{ opt.label }}

        p.text-red-600.dark_text-red-400(v-if="error") {{ error }}
        p.text-gray-500(v-else-if="!loading && jobs.length === 0") No jobs found.

        .flex.flex-col.gap-3(v-if="jobs.length > 0")
          .rounded-lg.border.border-gray-200.dark_border-gray-600.bg-gray-50.dark_bg-gray-800.overflow-hidden(
            v-for="job in decoratedJobs"
            :key="job.batchId"
            :class="cardBorder(job)"
          )
            .flex.items-stretch.divide-x.divide-gray-200.dark_divide-gray-600

              //- Meta column
              .flex.flex-col.justify-start.gap-1.px-4.py-3.w-56.shrink-0
                .text-sm.font-semibold.text-gray-900.dark_text-gray-100 {{ formatDate(job.submittedAt) }}
                .text-xs.text-gray-500.dark_text-gray-400 {{ job.submittedBy }}
                .flex.gap-1.mt-1
                  span.text-xs.px-2.py-px.rounded.bg-gray-200.dark_bg-gray-700.text-gray-700.dark_text-gray-300 {{ job.provider }}
                  span.text-xs.px-2.py-px.rounded.bg-blue-100.dark_bg-blue-900.text-blue-700.dark_text-blue-300(v-if="job.refreshAi") force refresh
                  span.text-xs.px-2.py-px.rounded.bg-yellow-100.dark_bg-yellow-800.text-yellow-700.dark_text-yellow-300(v-if="+job.inProgress > 0") ⏳ running

              //- Base column (if base courses exist)
              //- Grid: left = label/chips/stats  right = full-height scrollable errors
              .flex-1.px-4.py-3.flex.gap-4.items-stretch(
                v-if="job.baseCourses && job.baseCourses.length"
              )
                .flex.flex-col.justify-between.gap-2
                  div
                    .text-xs.font-medium.text-gray-500.dark_text-gray-400.uppercase.tracking-wide.mb-1 Base
                    .flex.flex-wrap.items-center.gap-1.mb-1
                      span.text-xs.text-gray-500.dark_text-gray-400.me-1 Courses:
                      span.text-xs.px-2.py-px.rounded.bg-blue-100.dark_bg-blue-900.text-blue-700.dark_text-blue-300(
                        v-for="c in job.baseCourses" :key="c"
                      ) {{ c }}
                    .flex.flex-wrap.items-center.gap-1(v-if="job.languages && job.languages.length")
                      span.text-xs.text-gray-500.dark_text-gray-400.me-1 Languages:
                      span.text-xs.px-2.py-px.rounded.bg-gray-100.dark_bg-gray-700.text-gray-600.dark_text-gray-400(
                        v-for="l in job.languages" :key="l"
                      ) {{ l }}
                  .flex.gap-4
                    .flex.flex-col.items-center
                      span.text-base.font-bold.text-green-600.dark_text-green-400 {{ job.baseCompleted }}
                      span.text-xs.text-gray-400 Done
                    .flex.flex-col.items-center
                      span.text-base.font-bold(:class="+job.baseFailed > 0 ? 'text-red-600 dark_text-red-400' : 'text-gray-300'") {{ job.baseFailed }}
                      span.text-xs.text-gray-400 Failed
                    .flex.flex-col.items-center
                      span.text-base.font-bold(:class="job.baseSkipped > 0 ? 'text-amber-600 dark_text-amber-400' : 'text-gray-300'") {{ job.baseSkipped }}
                      span.text-xs.text-gray-400 Skipped
                .flex-1.overflow-y-auto.max-h-32.hidden.lg_block(v-if="job.baseErrorGroups.length")
                  .leading-relaxed(
                    v-for="(g, index) in job.baseErrorGroups"
                    :key="index"
                    :class="index < job.baseErrorGroups.length - 1 ? 'mb-2' : ''"
                  )
                    .text-xs.font-semibold(:class="g.skipped ? 'text-amber-600 dark_text-amber-400' : 'text-red-600 dark_text-red-400'") {{ g.count }} {{ g.skipped ? 'skipped' : 'failed' }} — {{ reasonLabel(g.reason) }}
                    .text-xs.text-gray-500.dark_text-gray-400.break-words {{ g.sample }}

              //- Customisation column (if firms exist)
              //- Grid: left = label/chips/stats  right = full-height scrollable errors
              .flex-1.px-4.py-3.flex.gap-4.items-stretch(
                v-if="job.firms && job.firms.length"
              )
                .flex.flex-col.justify-between.gap-2
                  div
                    .text-xs.font-medium.text-gray-500.dark_text-gray-400.uppercase.tracking-wide.mb-1 Customisations
                    .flex.flex-wrap.items-center.gap-1.mb-1(v-if="job.firms && job.firms.length")
                      span.text-xs.text-gray-500.dark_text-gray-400.me-1 Firms:
                      span.text-xs.px-2.py-px.rounded.bg-purple-100.dark_bg-purple-900.text-purple-700.dark_text-purple-300(
                        v-for="f in job.firms" :key="f"
                      ) {{ f }}
                    .flex.flex-wrap.items-center.gap-1.mb-1(v-if="job.customCourses && job.customCourses.length")
                      span.text-xs.text-gray-500.dark_text-gray-400.me-1 Courses:
                      span.text-xs.px-2.py-px.rounded.bg-gray-100.dark_bg-gray-700.text-gray-600.dark_text-gray-400(
                        v-for="c in job.customCourses" :key="c"
                      ) {{ c }}
                    .flex.flex-wrap.items-center.gap-1(v-if="job.languages && job.languages.length")
                      span.text-xs.text-gray-500.dark_text-gray-400.me-1 Languages:
                      span.text-xs.px-2.py-px.rounded.bg-gray-100.dark_bg-gray-700.text-gray-600.dark_text-gray-400(
                        v-for="l in job.languages" :key="l"
                      ) {{ l }}
                  .flex.gap-4
                    .flex.flex-col.items-center
                      span.text-base.font-bold.text-green-600.dark_text-green-400 {{ job.customCompleted }}
                      span.text-xs.text-gray-400 Done
                    .flex.flex-col.items-center
                      span.text-base.font-bold(:class="+job.customFailed > 0 ? 'text-red-600 dark_text-red-400' : 'text-gray-300'") {{ job.customFailed }}
                      span.text-xs.text-gray-400 Failed
                    .flex.flex-col.items-center
                      span.text-base.font-bold(:class="job.customSkipped > 0 ? 'text-amber-600 dark_text-amber-400' : 'text-gray-300'") {{ job.customSkipped }}
                      span.text-xs.text-gray-400 Skipped
                .flex-1.overflow-y-auto.max-h-32.hidden.lg_block(v-if="job.customErrorGroups.length")
                  .leading-relaxed(
                    v-for="(g, index) in job.customErrorGroups"
                    :key="index"
                    :class="index < job.customErrorGroups.length - 1 ? 'mb-2' : ''"
                  )
                    .text-xs.font-semibold(:class="g.skipped ? 'text-amber-600 dark_text-amber-400' : 'text-red-600 dark_text-red-400'") {{ g.count }} {{ g.skipped ? 'skipped' : 'failed' }} — {{ reasonLabel(g.reason) }}
                    .text-xs.text-gray-500.dark_text-gray-400.break-words {{ g.sample }}

              //- Total column — with report download below the count
              .flex.flex-col.items-center.justify-center.gap-1.px-5.py-3.w-24.shrink-0
                span.text-xl.font-bold.text-gray-900.dark_text-gray-100 {{ job.total }}
                span.text-xs.text-gray-400 Total
                button.flex.items-center.gap-1.text-xs.mt-1.px-2.py-1.rounded.border.border-gray-300.dark_border-gray-600.bg-white.dark_bg-gray-800.hover_bg-gray-100.dark_hover_bg-gray-700.text-gray-700.dark_text-gray-200(
                  v-if="job.failedBaseItems.length || job.failedCustomItems.length"
                  type="button"
                  title="Download a CSV report of failed and skipped items"
                  @click="downloadReport(job)"
                )
                  Icon(icon="ph:download-simple")
                  span Report

              //- Retry column — only shown when there are failures and job is not running
              .flex.flex-col.items-center.justify-center.px-4.py-3.shrink-0(
                v-if="(job.baseFailed > 0 || job.customFailed > 0) && +job.inProgress === 0"
              )
                button.text-xs.px-3.py-1.rounded.bg-orange-500.hover_bg-orange-600.text-white.font-semibold.disabled_opacity-50.disabled_cursor-not-allowed(
                  type="button"
                  :disabled="retrying === job.batchId || retried.has(job.batchId)"
                  @click="retry(job)"
                ) {{ retrying === job.batchId ? 'Retrying…' : retried.has(job.batchId) ? 'Retried' : 'Retry' }}
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import {
  Menu as HeadlessMenu,
  MenuButton,
  MenuItems,
  MenuItem
} from '@headlessui/vue';
import { Icon } from '@iconify/vue';
import MainMenu from './MainMenu.vue';
import ToggleDark from './ToggleDark.vue';
import useDocTitle from '../lib/useDocTitle';
import {
  getBulkTranslationJobs,
  retryBulkTranslationJob,
  type BulkTranslationJobSummary,
  type BulkTranslationFailedItem
} from '../lib/callModContentAuthors';
import { downloadCSV } from '../lib/createResportCSV';

useDocTitle(ref('Bulk Translation Jobs'));

const jobs = ref<BulkTranslationJobSummary[]>([]);
const loading = ref(false);
const error = ref('');
const retrying = ref<string | null>(null);
// batchIds we've already kicked off a retry for, so we don't spawn duplicate jobs.
const retried = ref<Set<string>>(new Set());
const scrollContainer = ref<HTMLElement | null>(null);

// Auto-refresh: 0 = off, otherwise interval in ms.
const autoRefreshOptions = [
  { label: 'Auto-refresh off', value: 0 },
  { label: 'Every 30s', value: 30_000 },
  { label: 'Every 1m', value: 60_000 },
  { label: 'Every 5m', value: 300_000 },
  { label: 'Every 15m', value: 900_000 }
];
const autoRefreshMs = ref(0);
let autoRefreshTimer: ReturnType<typeof setInterval> | null = null;

const autoRefreshLabel = computed(() =>
  autoRefreshMs.value === 0
    ? 'Off'
    : autoRefreshOptions
        .find(o => o.value === autoRefreshMs.value)
        ?.label.replace('Every ', '')
);

function setAutoRefresh(ms: number) {
  autoRefreshMs.value = ms;
  if (autoRefreshTimer) {
    clearInterval(autoRefreshTimer);
    autoRefreshTimer = null;
  }
  if (ms > 0) autoRefreshTimer = setInterval(load, ms);
}

onUnmounted(() => {
  if (autoRefreshTimer) clearInterval(autoRefreshTimer);
});

async function load() {
  loading.value = true;
  error.value = '';
  try {
    jobs.value = await getBulkTranslationJobs();
  } catch (e: any) {
    error.value = `Failed to load jobs: ${e.message}`;
  } finally {
    loading.value = false;
  }
}

function formatDate(iso: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

async function retry(job: BulkTranslationJobSummary) {
  retrying.value = job.batchId;
  try {
    await retryBulkTranslationJob(job);
    // Mark as retried so the button stays disabled — a new job is now in the list.
    retried.value.add(job.batchId);
    await load();
    scrollContainer.value?.scrollTo({ top: 0, behavior: 'smooth' });
  } catch (e: any) {
    error.value = `Failed to retry job: ${e.message}`;
  } finally {
    retrying.value = null;
  }
}

function cardBorder(job: BulkTranslationJobSummary) {
  if (+job.inProgress > 0) return 'border-l-4 border-l-yellow-400';
  if (+job.failed > 0) return 'border-l-4 border-l-red-400';
  return 'border-l-4 border-l-green-400';
}

// The API only writes skipped items with these reasons, so we can tell a skip
// from a genuine failure within the mixed failed* arrays.
const SKIP_REASONS = new Set([
  'base_failed_in_batch',
  'base_translation_failed'
]);

const REASON_LABELS: Record<string, string> = {
  base_failed_in_batch: 'Base course failed or skipped in this batch',
  base_translation_failed: 'Base course translation failed',
  unsaved_draft: 'Customisation has an unsaved draft',
  draft_check_error: 'Draft check failed',
  not_found: 'Course not found (404)',
  draft_conflict: 'Draft conflict (409)',
  client_error: 'Client error (4xx)',
  max_attempts_exceeded: 'Failed after max retries'
};

function reasonLabel(reason: string | null) {
  return (reason && REASON_LABELS[reason]) || reason || 'Unknown error';
}

// failed* arrays hold both failed and skipped items; skipped = length − failed.
function skipCount(items: BulkTranslationFailedItem[], failed: number) {
  return Math.max(0, (items?.length || 0) - (+failed || 0));
}

// Collapse a failed/skipped list into one row per (skipped, reason) so a job
// with dozens of identical errors shows a handful of summary lines instead.
function errorGroups(items: BulkTranslationFailedItem[]) {
  const groups = new Map<
    string,
    { skipped: boolean; reason: string | null; count: number; sample: string }
  >();
  for (const it of items || []) {
    const skipped = SKIP_REASONS.has(it.reason || '');
    const key = `${skipped}|${it.reason || ''}`;
    let g = groups.get(key);
    if (!g) {
      g = {
        skipped,
        reason: it.reason,
        count: 0,
        sample: it.error || it.reason || ''
      };
      groups.set(key, g);
    }
    g.count++;
  }
  return [...groups.values()].sort((a, b) => b.count - a.count);
}

// Escape a single CSV field: wrap in quotes (doubling any inner quotes) only when
// the value contains a comma, quote, or newline — error text routinely does.
function csvCell(value: unknown): string {
  const s = value == null ? '' : String(value);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

// Build a CSV report of every failed/skipped item in a job. Successful items are
// only available server-side as counts, so the report covers failures + skips.
function buildReportCsv(job: BulkTranslationJobSummary): string {
  const headers = [
    'Batch ID',
    'Submitted At',
    'Submitted By',
    'Provider',
    'Scope',
    'Course ID',
    'Firm Code',
    'Language',
    'Status',
    'Reason',
    'Reason (readable)',
    'Error'
  ];
  const rows: string[][] = [];
  const addItems = (items: BulkTranslationFailedItem[], scope: string) => {
    for (const it of items || []) {
      const status = SKIP_REASONS.has(it.reason || '') ? 'skipped' : 'failed';
      rows.push([
        job.batchId,
        job.submittedAt,
        job.submittedBy,
        job.provider,
        scope,
        String(it.courseId),
        it.firmCode || '',
        it.lang,
        status,
        it.reason || '',
        reasonLabel(it.reason),
        it.error || ''
      ]);
    }
  };
  addItems(job.failedBaseItems, 'base');
  addItems(job.failedCustomItems, 'customisation');
  // Lead with a BOM so Excel reads the UTF-8 content correctly.
  return (
    '﻿' + [headers, ...rows].map(r => r.map(csvCell).join(',')).join('\r\n')
  );
}

function downloadReport(job: BulkTranslationJobSummary) {
  const date = (job.submittedAt || new Date().toISOString()).split('T')[0];
  downloadCSV(buildReportCsv(job), `bulk-translation-report-${date}.csv`);
}

// Pre-compute derived stats per job so the template stays declarative.
const decoratedJobs = computed(() =>
  jobs.value.map(job => ({
    ...job,
    baseSkipped: skipCount(job.failedBaseItems, job.baseFailed),
    customSkipped: skipCount(job.failedCustomItems, job.customFailed),
    baseErrorGroups: errorGroups(job.failedBaseItems),
    customErrorGroups: errorGroups(job.failedCustomItems)
  }))
);

onMounted(load);
</script>
